import React, { useState } from 'react';
import { BookOpen, Info, X, ClipboardCheck, LayoutList, FileText, Target } from 'lucide-react';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import ResultGauge from '../ui/ResultGauge';
import TargetRoadmap from '../common/TargetRoadmap';
import { calculateTheoryGPA, calculateGradeTargets, parseFloatLoose0 } from '../../utils/gpaLogic';
import { toast } from 'react-toastify';

const TheoryCalculator = () => {
  const [showHelp, setShowHelp] = useState(true);
  const [data, setData] = useState({
    assignmentObtained: '',
    assignmentTotal: '10',
    quizObtained: '',
    quizTotal: '15',
    midtermObtained: '',
    midtermTotal: '25',
    finalObtained: '',
    finalTotal: '50'
  });
  
  const [result, setResult] = useState(null);

  const handleCalculate = () => {
    try {
      const res = calculateTheoryGPA(data);
      setResult(res);
      toast.success("Theory GPA calculated!");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const calculateCurrentProgress = () => {
    const assignObt = parseFloatLoose0(data.assignmentObtained);
    const assignTot = parseFloatLoose0(data.assignmentTotal);
    const quizObt = parseFloatLoose0(data.quizObtained);
    const quizTot = parseFloatLoose0(data.quizTotal);
    const midObt = parseFloatLoose0(data.midtermObtained);
    const midTot = parseFloatLoose0(data.midtermTotal);

    const assignPct = assignTot > 0 ? (assignObt / assignTot) * 10 : 0;
    const quizPct = quizTot > 0 ? (quizObt / quizTot) * 15 : 0;
    const midPct = midTot > 0 ? (midObt / midTot) * 25 : 0;

    return assignPct + quizPct + midPct;
  };

  const currentWeighted = calculateCurrentProgress();
  const targets = calculateGradeTargets(currentWeighted, 50, parseFloatLoose0(data.finalTotal) || 50);
  const hasStartedEntries = data.midtermObtained !== '' || data.quizObtained !== '';

  const components = [
    { id: 'assignment', icon: ClipboardCheck, label: 'Assignments', weight: '10%', color: 'indigo' },
    { id: 'quiz', icon: LayoutList, label: 'Quizzes', weight: '15%', color: 'purple' },
    { id: 'midterm', icon: FileText, label: 'Midterm', weight: '25%', color: 'blue' },
    { id: 'final', icon: Target, label: 'Final Exam', weight: '50%', color: 'emerald' },
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {showHelp && (
        <div className="p-5 bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 rounded-3xl flex items-start gap-4 relative transition-all animate-in slide-in-from-top-4">
          <div className="p-2 bg-white dark:bg-slate-900 rounded-xl text-blue-600 shadow-sm flex-shrink-0">
            <Info size={20} />
          </div>
          <div className="text-sm">
            <p className="font-extrabold text-blue-900 dark:text-blue-200 uppercase tracking-tight mb-1">Teacher Specifics</p>
            <p className="text-blue-700 dark:text-blue-300 font-medium">
              Teachers might vary totals (e.g., Final out of 70). Enter your <span className="font-bold">Obtained</span> and <span className="font-bold">Total</span> marks exactly as they appear in your CU portal.
            </p>
          </div>
          <button onClick={() => setShowHelp(false)} className="absolute top-4 right-4 text-blue-300 hover:text-blue-600 transition-colors">
            <X size={18} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {components.map((comp) => (
          <div key={comp.id} className={`bg-white dark:bg-[#0f1535] p-6 rounded-[2.5rem] border border-slate-100 dark:border-indigo-500/10 shadow-sm transition-all hover:shadow-md`}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-${comp.color}-50 dark:bg-${comp.color}-500/10 text-${comp.color}-600 rounded-xl`}>
                  <comp.icon size={18} />
                </div>
                <h4 className="font-black text-slate-800 dark:text-white tracking-tight">{comp.label}</h4>
              </div>
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 uppercase tracking-widest`}>
                {comp.weight}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Obtained</label>
                <Input 
                  value={data[`${comp.id}Obtained`]}
                  onChange={(e) => setData({...data, [`${comp.id}Obtained`]: e.target.value})}
                  placeholder="0"
                  inputMode="decimal"
                  className="text-center font-bold"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Total</label>
                <Input 
                  value={data[`${comp.id}Total`]}
                  onChange={(e) => setData({...data, [`${comp.id}Total`]: e.target.value})}
                  placeholder="0"
                  inputMode="decimal"
                  className="text-center font-bold"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasStartedEntries && (
        <div className="bg-slate-50 dark:bg-white/5 p-2 rounded-[2.5rem]">
          <TargetRoadmap targets={targets} finalTotal={parseFloatLoose0(data.finalTotal) || 50} />
        </div>
      )}

      <div className="flex flex-col items-center gap-8 pt-4">
        <Button onClick={handleCalculate} className="w-full md:w-auto px-16 py-4 rounded-2xl shadow-lg shadow-indigo-500/20">
          Calculate Subject GPA
        </Button>

        {result && (
          <div className="w-full max-w-2xl animate-in zoom-in-95 duration-500">
            <div className="bg-white dark:bg-[#0b0f2a] p-8 rounded-[3rem] border border-slate-100 dark:border-indigo-500/10 shadow-2xl relative overflow-hidden flex flex-col items-center">
              <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <ResultGauge 
                  value={result.gpa} 
                  label="Theory GPA" 
                  subLabel={`${result.percentageRaw.toFixed(1)}% Combined`}
                  size={240}
                />
                
                <div className="space-y-4">
                  <h4 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-4 text-center md:text-left">Breakdown</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(result.breakdown).map(([name, val]) => (
                      <div key={name} className="p-3 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-100 dark:border-white/5">
                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">{name}</span>
                        <span className="font-bold text-slate-900 dark:text-white">{val.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 bg-indigo-600 rounded-2xl text-white text-center">
                    <span className="text-[10px] font-black uppercase tracking-widest block mb-1 opacity-70">Total Score</span>
                    <span className="text-2xl font-black">{result.percentageRaw.toFixed(2)} / 100</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TheoryCalculator;
